import { useAppScrollRef } from '@doltech/core/lib/appScroll/withAppScroll';
import { IconMultimediaPlayButtonO } from '@doltech/icons/IconMultimediaPlayButtonO';
import { components } from '@doltech/pageManagement/dto';
import { PageObjectContent } from '../../../../shared/components/PageObjectContent/PageObjectContent';
import { RelatedPostsDolVn } from '../../../../shared/components/RelatedPosts/RelatedPostsDolVn';
import { PageTypeEnum, SkillEnum, TestTypeEnum } from '@doltech/utils/lib/constants';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import * as React from 'react';
import { useQuery } from 'react-query';
import styled from 'styled-components';

import { deviceBreakpoints } from '../../../../hooks/useDeviceQuery.hook';
import { RelatedPostCardModel } from '../../../../shared/components/RelatedPosts/RelatedPostCard';
import { SupportedSocialAction } from '../../../Blog/BlogLayout';
import { BlogTableOfContent } from '../../../Blog/BlogTableOfContent';
import { colorsV2 } from '../../../colors-v2';
import { Box } from '../../../Common/BoxV2';
import { Divider } from '../../../Divider/Divider';
import { Link } from '../../../Link';
import { ReferralShareBanner } from '../../../ReferralShare/ReferralShareBanner';
import { trackingClick } from '../../../SearchEngine/api/search-api';
import { Separator } from '../Content/Separator';
import { useScrollToLocationHashOnMount } from '../Content/useScrollToLocationHashOnMount';
import { LandingHeaderSEO } from '../Header/HeaderSEO';
import { LandingLayout, LandingLayoutContent } from '../LandingLayout';
import {
  LandingLayoutWithSEOHeaderContext,
  LandingLayoutWithSEOHeaderProps,
} from '../LandingLayoutWithSEOHeaderContext';
import { LandingSideBar } from '../LandingSidebar/LandingSidebar';
import { useLandingSharedDataStore } from '../model/landing-shared-data.hook';
import { LandingHeaderFullWidth } from '../PremadeHeader/LandingHeaderFullWidth';
import { BlogSocialButton } from '../SocialButton/BlogSocialButton';

const InteractionBottomNavigation = dynamic<any>(
  import('../BottomNavigation/InteractionBottomNavigation').then(
    (mod) => mod.InteractionBottomNavigation
  ),
  { ssr: false }
);
const BlogInteractionSidebar = dynamic<any>(
  import('../../../Blog/BlogInteractionSidebar').then((mod) => mod.BlogInteractionSidebar),
  { ssr: false }
);

const CONTENT_MIDDLE_MAX_WIDTH = 712;

const Main = styled.div.withConfig({
  componentId: 'LandingLayoutWithSEOHeader_Main',
} as any)`
  #landing-layout {
    background-color: ${colorsV2.white100};
  }

  .landing-content {
    padding-bottom: 80px;
  }

  .section-middle-container {
    .table-of-content,
    .section-middle-content {
      max-width: ${CONTENT_MIDDLE_MAX_WIDTH}px;
      margin: 0 auto;
    }
    .table-of-content {
      margin-bottom: 24px;
    }
    padding: 0 16px;
  }

  @media ${deviceBreakpoints.fromDesktop} {
    .landing-content {
      padding-bottom: 104px;
    }
    .section-middle-container {
      #table-of-content {
        width: 214px;
        min-width: 214px;
        max-width: 214px;
        margin: 0 auto;
        margin-bottom: 0;
      }
    }
  }
`;

const FooterSeparator = styled.div`
  @media ${deviceBreakpoints.fromDesktop} {
    display: none;
  }
`;

interface LeftInteractionSidebarProps {
  testType?: TestTypeEnum;
  testSkill?: SkillEnum;
  seoData?: components['schemas']['PageResponseDTOObject'];
  pageData?: components['schemas']['SEOPageDTO'][];
}

interface ContentFooterProps {
  testType?: TestTypeEnum;
  testSkill?: SkillEnum;
  pageData?: components['schemas']['SEOPageDTO'][];
  relatedPosts?: RelatedPostCardModel[];

  banner: any;
  bannerLink: string;
}

interface SideBarDataProps {
  title: string;
  description: string;
  actions: SupportedSocialAction[];
}

const getInteractionBySeoType = (testType, skill): SideBarDataProps => {
  switch (testType) {
    case TestTypeEnum.FULL_TEST: {
      return {
        title: 'IELTS Online Test',
        description:
          'Gồm làm đề, xem giải thích chi tiết, học từ vựng của những bài thi IELTS Reading & Listening phổ biến nhất trên thị trường',
        actions: ['copy-link', 'facebook', 'clap', 'do-test'],
      };
    }
    case TestTypeEnum.PRACTICE_TEST: {
      if (SkillEnum.READING === skill) {
        return {
          title: 'IELTS Reading Practice',
          description:
            'Gồm làm đề, xem giải thích chi tiết, học từ vựng của những bài thi IELTS Reading phổ biến nhất trên thị trường',
          actions: ['copy-link', 'facebook', 'clap', 'do-test'],
        };
      }
      return {
        title: 'IELTS Listening Practice',
        description:
          'Gồm làm đề, xem giải thích chi tiết, học từ vựng của những bài thi IELTS Listening phổ biến nhất trên thị trường',
        actions: ['copy-link', 'facebook', 'clap', 'do-test'],
      };
    }
    default: {
      return {
        title: 'IELTS Online Test',
        description:
          'Gồm làm đề, xem giải thích chi tiết, học từ vựng của những bài thi IELTS Reading & Listening phổ biến nhất trên thị trường',
        actions: ['copy-link', 'facebook', 'clap'],
      };
    }
  }
};

const getLandingPageUrl = (testType, skill, pageLink) => {
  if (testType === TestTypeEnum.FULL_TEST) {
    return pageLink?.staticPageLink[PageTypeEnum.LANDING_FULL_TEST]?.url;
  }
  if (testType === TestTypeEnum.PRACTICE_TEST) {
    if (SkillEnum.LISTENING === skill) {
      return pageLink?.staticPageLink[PageTypeEnum.LANDING_LISTENING_PRACTICE_TEST]?.url;
    }
    if (SkillEnum.READING === skill) {
      return pageLink?.staticPageLink[PageTypeEnum.LANDING_READING_PRACTICE_TEST]?.url;
    }
  }
  return null;
};

const LeftInteractionSidebar = observer((props: LeftInteractionSidebarProps) => {
  const { pageLink } = useLandingSharedDataStore();

  const { pageData, seoData, testType, testSkill } = props;
  const sidebarData = getInteractionBySeoType(testType, testSkill);
  const { appScrollRef } = useAppScrollRef();

  return (
    <BlogInteractionSidebar
      stickyId="left-interaction-sidebar"
      scrollContainerElm={appScrollRef.current}
      stickOnElm=".section-middle-content"
      hideWhenIntersectWithElms={['#content-footer', '#compact-footer', '.common-footer']}
      offsetY={144}
      stickOnElmPosition={{
        align: 'left',
        gapInPx: 60,
      }}
      stickyMode="ALWAYS_SHOW"
      title={
        <Link href={getLandingPageUrl(testType, testSkill, pageLink)}>{sidebarData.title}</Link>
      }
      description={sidebarData.description}
      socials={
        <BlogSocialButton
          align="vertical"
          actions={sidebarData.actions}
          showText
          seoData={seoData}
          pageData={pageData}
        />
      }
    />
  );
});

const ContentFooter = (props: ContentFooterProps) => {
  const { pageData, relatedPosts, testType, testSkill } = props;

  const { pageLink } = useLandingSharedDataStore();

  const sidebarData = getInteractionBySeoType(testType, testSkill);

  const actionDoTest = pageData.find((item) => item.templateTypeId === PageTypeEnum.DO_TEST);

  return (
    <Box position="relative" id="content-footer" bg="gray5" p="40px 0">
      <div style={{ position: 'relative' }} className="section-middle-content">
        <PageObjectContent
          title={sidebarData?.title}
          description={sidebarData?.description}
          url={actionDoTest?.url}
          actionText="Làm bài"
          actionProps={{
            iconLeft: <IconMultimediaPlayButtonO />,
          }}
        />
        <Divider.Line color={colorsV2.black20} space={32} />
        {relatedPosts && (
          <RelatedPostsDolVn
            data={relatedPosts}
            readMore={{
              url: getLandingPageUrl(testType, testSkill, pageLink),
            }}
            metadata={[]}
          />
        )}
        {/* <DolenglishPromotionRightSideBar
          imgVertical={banner.imgVertical}
          imgHorizontal={banner.imgHorizontal}
          href={bannerLink || '/'}
        /> */}
      </div>
    </Box>
  );
};

const LandingLayoutWithSEOHeaderContent = (props: LandingLayoutWithSEOHeaderProps) => {
  const {
    banner,
    bannerLink,
    children,
    testId,
    testSkill,
    testType,
    seoData,
    pageData,
    tableOfContentItems,
    ...rest
  } = props;
  const { pageDetail, relatedPosts } = pageData;

  const { appScrollRef } = useAppScrollRef();
  useQuery<any>(
    ['trackingClick', seoData?.id],
    () => trackingClick({ query: seoData?.dol?.title, document_id: seoData?.id }),
    {
      enabled: typeof window !== 'undefined' && seoData?.id != null && seoData?.dol?.title != null,
    }
  );

  useScrollToLocationHashOnMount();

  return (
    <LandingLayoutWithSEOHeaderContext.Provider value={{ ...props }}>
      <Main {...rest}>
        <LandingLayout defaultSidebarOpen={false}>
          <LandingHeaderFullWidth id="landing-header" />
          <LandingLayoutContent>
            <ReferralShareBanner
              className="landing-layout-with-seo-header"
              pageUrl={seoData?.url}
            />
            <LandingLayoutContent.LeftOverlaySidebar displayAsSideBar>
              <LandingSideBar />
            </LandingLayoutContent.LeftOverlaySidebar>
            <LandingLayoutContent.SectionMiddle>
              <LandingHeaderSEO
                seoData={seoData}
                seoPageData={pageDetail.pages || []}
                testId={testId}
                testType={testType}
                testSkill={testSkill}
              />
              <div className="section-middle-container">
                <LeftInteractionSidebar
                  seoData={seoData}
                  testType={testType}
                  testSkill={testSkill}
                  pageData={pageDetail.pages || []}
                />
                {tableOfContentItems?.length > 0 && (
                  <>
                    <BlogTableOfContent
                      title="Mục lục"
                      tocTitle={(seoData?.dol as any)?.tocTitle || seoData?.dol?.title}
                      items={tableOfContentItems}
                      stickyId="table-of-content"
                      scrollContainerElm={appScrollRef.current}
                      stickOnElm=".section-middle-content"
                      stickyMode="ALWAYS_SHOW"
                      hideWhenIntersectWithElms={[
                        '#compact-footer',
                        '#content-footer',
                        '#test-section-download-pdf',
                        '#footer-author-info',
                        '.footer-with-register',
                        '.common-footer',
                      ]}
                      stickOnElmPosition={{
                        align: 'right',
                        gapInPx: 24,
                      }}
                    />
                    <FooterSeparator>
                      <Separator my="24px" variant="three-dot-horizontal" />
                    </FooterSeparator>
                  </>
                )}

                <div className="section-middle-content">{children}</div>

                <ContentFooter
                  testType={testType}
                  testSkill={testSkill}
                  pageData={pageDetail.pages || []}
                  relatedPosts={relatedPosts}
                  banner={banner}
                  bannerLink={bannerLink}
                />

                <InteractionBottomNavigation
                  layout="responsive"
                  pageId={seoData?.id}
                  pageUrl={seoData?.url}
                  scrollContainerElm={appScrollRef.current}
                  tocTitle={(seoData?.dol as any)?.tocTitle || seoData?.dol?.title}
                  tocItems={tableOfContentItems}
                />
              </div>
            </LandingLayoutContent.SectionMiddle>
          </LandingLayoutContent>
        </LandingLayout>
      </Main>
    </LandingLayoutWithSEOHeaderContext.Provider>
  );
};

export const LandingLayoutWithSEOHeader = LandingLayoutWithSEOHeaderContent;
